import React, { createContext, useContext, useEffect, useState } from 'react';
import { App } from 'antd';
import { deleteUser, getAuth, getRedirectResult, GoogleAuthProvider, signOut } from 'firebase/auth';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { InsMonitorModel } from '@/types/home';
import { useTranslation } from 'next-i18next';
import { getPersionalProductApi, getUserInfoApi, getUserUseTimesApi, userUpdate } from '@/lib/service';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { logLoginModelGA } from '@/utils/loginModelGA';

type UserProviderType = {
  /**
   * total cart quantity
   */
  // total?: number;
  openLogin?: boolean;
  isLoginView?: boolean;
  openLoginResult?: boolean;
  accountList?: InsMonitorModel[];
  /**
   * add cart callback function
   * @param params Product information added
   */
  onAddShop?: <P = any>(params: P) => Promise<{ data: P }>;
  onDeleteShopCarItem?: <P = any>(params: P) => Promise<{ data: P }>;
  onOpenLoginModal?: (open: boolean, callback?: (() => void) | 'loginSuccessfully') => void;
  onChangedLoginView?: (isLoginView: boolean) => void;
  onChangedLoginResult?: (isLoginView: boolean) => void;
  onAddToken?: (token?: string, signInMethod?: string, idToken?: string) => void;
  onInitAccountList?: (accountList?: InsMonitorModel[]) => void;
  onAddAccount?: (account?: InsMonitorModel) => void;
  onSignOut?: (showAlert?: boolean) => void;
  onDeleteUser?: () => void;

  /**
   * Refresh cart data
   */
  onRefreshCartData?: () => Promise<void>;

  isLogin?: boolean;
  userInfo?: any;
  setUserInfo?: any;
  userSub?: any;
  UpDateUserSub?: any;
  userUse?: any;
  UpdateUserUs?: any;
};

// 将loginCallbackMap从数组改为Map对象，key为自增id，value为回调函数
const loginCallbackMap = new Map<number, () => void>();
let loginCallbackId = 0; // 用于生成唯一id

const Context = createContext<UserProviderType>({});

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const router = useRouter();
  // const [totalCartQuantity, setTotalCartQuantity] = useState<number>();
  const [openLogin, setOpenLogin] = useState<boolean>(false);
  const [isLoginView, setIsLoginView] = useState<boolean>(true); // 切换登录与注册
  const [openLoginResult, setOpenLoginResult] = useState<boolean>(false);
  const [accountList, setAccountList] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [userSub, setUserSub] = useState<any>({});

  // 登录状态：SSR 和 hydration 阶段统一为 false，useEffect 后才更新为真实值
  // 这样避免水合错误（SSR 和 CSR 的 DOM 结构一致）
  const [isLogin, setIsLogin] = useState<boolean>(false);

  // 在 hydration 后检查登录状态并获取用户信息
  useEffect(() => {
    const token = Cookies.get('userToken');
    const loginStatus = token != null && token != undefined && token.length > 0;
    setIsLogin(loginStatus);

    if (loginStatus) {
      getUserInfoApi().then((res) => {
        setUserInfo(res.data);
        localStorage.setItem('uif', JSON.stringify(res.data));
      });
    }
  }, [isLogin]);

  useEffect(() => {
    if (isLogin && !userSub?.item_id) {
      getPersionalProductApi().then((res) => {
        if (res.data?.length > 0) {
          setUserSub(res.data[0]);
        }
      });
    }
  }, [isLogin]);
  const UpDateUserSub = () => {
    getPersionalProductApi().then((res) => {
      setUserSub(res?.data[0]);
    });
  };
  // const token1 = ls_getItem('userToken');
  // const [isLogin, setIsLogin] = useState<boolean>((token != null && token != undefined && token.length > 0) || (token1 != null && token1 != undefined && token1.length > 0));

  const handleAddShopCarItem = async (params: any) => {
    return Promise.resolve(params);
  };

  useEffect(() => {
    // useEffect 只在客户端执行，无需 typeof window 检查
    let timer = setTimeout(() => {
      loadUserData();
      clearTimeout(timer);
    }, 100);
  }, []);

  const getToken = async (id: string, signInMethod: string) => {
    const res: any = await userUpdate(id, router.locale);
    if (res?.data?.first_login) {
      if (res.code == 0) {
        ga_logEvent(EventName.sign_up);
      } else {
        ga_logEvent(EventName.user_register_Fail);
      }
    }

    if (res.code == 0) {
      localStorage.removeItem('source');
      onAddToken(res.data.token, signInMethod, id);
      getUserInfoApi().then((res) => {
        setUserInfo(res.data);
        localStorage.setItem('uif', JSON.stringify(res.data));
      });
      onOpenLoginModal(false);
      onChangedLoginResult(true);
      ga_logEvent(EventName.user_Login_Success);
    } else {
      message.error(res.message);
    }
  };

  const loadUserData = () => {
    const auth = getAuth();
    getRedirectResult(auth)
      .then(async (result) => {
        if (result) {
          // 只使用 ID Token，accessToken 未使用因此不存储
          result.user.getIdToken().then((id) => {
            getToken(id, 'google');
            ga_logEvent(EventName.login, { method: 'Google' });
          });
        }
      })
      .catch((error) => {
        // 静默处理 getRedirectResult 错误，避免在非重定向场景下显示错误提示
        // 这是正常行为：用户直接访问页面时，getRedirectResult 会返回 null 或抛出错误
        console.debug('getRedirectResult error (expected on normal page load):', error.message);
      });
  };

  /**
   * 打开登录弹窗，并处理回调逻辑
   * @param open 是否打开弹窗
   * @param callback 回调函数或字符串
   * @returns 如果open为true且callback为函数，返回一个带有cancel方法的侦听器对象
   */
  const onOpenLoginModal: UserProviderType['onOpenLoginModal'] = (open: boolean, callback) => {
    if (open) {
      logLoginModelGA();
    }
    setOpenLogin(open);

    // 如果open为true且callback为函数，则存入Map，并返回侦听器对象
    if (open && typeof callback === 'function') {
      loginCallbackId += 1;
      const currentId = loginCallbackId;
      loginCallbackMap.set(currentId, callback);
      // 返回侦听器对象，调用cancel可移除该回调
      return () => {
        loginCallbackMap.delete(currentId);
      };
    }
    // 如果callback为字符串且等于'loginSuccessfully'，则按顺序调用并移除所有回调
    if (callback === 'loginSuccessfully') {
      // 延迟调用
      setTimeout(() => {
        // 依次调用并移除所有回调
        loginCallbackMap.forEach((cb, id) => {
          try {
            cb();
          } catch (e) {
            // 捕获异常，防止单个回调影响后续
            // eslint-disable-next-line no-console
            console.error('登录回调执行异常', e);
          }
          loginCallbackMap.delete(id);
        });
      });
    }
  };

  const onChangedLoginView = (open: boolean) => {
    setIsLoginView(open);
  };

  const onChangedLoginResult = (open: boolean) => {
    UpDateUserSub();
    setOpenLoginResult(open);
  };

  const handleRefreshCartData = async () => {
    return new Promise<void>((resolve) => {
      message.success('Refresh successfully', 1.2, () => resolve(null));
    });
  };

  const onAddToken = (token?: string, signInMethod?: string, idToken?: string) => {
    if (token != undefined) {
      Cookies.set('userToken', token, { expires: 7 });
      Cookies.set('SignInMethod', signInMethod, { expires: 7 });
      Cookies.set('IdToken', idToken, { expires: 7 });
    } else {
      Cookies.remove('userToken');
      Cookies.remove('SignInMethod');
      Cookies.remove('IdToken');
    }
    setIsLogin(token != undefined);
  };

  const onAddAccount = (account?: InsMonitorModel) => {
    if (account != undefined) {
      setAccountList([...accountList, account]);
    }
  };

  const onInitAccountList = (accountList?: InsMonitorModel[]) => {
    setAccountList([...(accountList ?? [])]);
  };
  const clearAllCookies = () => {
    const cookies = document.cookie.split(';');
    // 遍历并删除每个cookie
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      Cookies.remove(name);
    }
    Cookies.remove('userToken');
  };

  const onSignOut = (showAlert?: boolean) => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        clearAllCookies();
        setUserInfo({});
        if (showAlert) {
          message.success(t('SignOutSuccessful'));
        }
        onAddToken(undefined);
        setUserSub({});
      })
      .catch((error) => {
        message.error(t('Log_Out_Failed_Error_Msg'));
      });
  };

  const onDeleteUser = () => {
    const auth = getAuth();
    const user = auth.currentUser;

    deleteUser(user)
      .then(() => {
        message.success(t('UserDeleted'));
        onAddToken(undefined);
        localStorage.clear();
        setAccountList([]);
        router.replace('/');
      })
      .catch((error) => {
        message.error(t('Account_Deletion_Failed_Error_Msg'));
      });
  };
  const [userUse, setuserUse] = useState({});
  useEffect(() => {
    if (isLogin) {
      getUserUseTimesApi().then((res) => {
        setuserUse(res.data);
      });
    }
  }, [isLogin]);

  const UpdateUserUs = () => {
    getUserUseTimesApi().then((res) => {
      setuserUse(res.data);
    });
  };

  const exposed = {
    openLogin,
    isLoginView,
    openLoginResult,
    // total: totalCartQuantity,
    isLogin, // 使用 state 而不是动态读取 Cookie，避免 Hydration 错误
    accountList,
    onAddShop: handleAddShopCarItem,
    onOpenLoginModal,
    onChangedLoginView,
    onChangedLoginResult,
    onRefreshCartData: handleRefreshCartData,
    onAddToken,
    onInitAccountList,
    onAddAccount,
    onSignOut,
    onDeleteUser,
    userInfo,
    setUserInfo,
    userSub,
    UpDateUserSub,
    userUse,
    UpdateUserUs,
  };

  return <Context.Provider value={exposed}>{children}</Context.Provider>;
};

export const useUserProvider = () => useContext(Context);

export default UserProvider;
